import { z } from 'zod';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { CHAT_UI_MAX_OFFERS } from '../../shared/schema/limits.js';
import { chatUiBase } from '../../shared/schema/base.js';

/** As coordenadas de uma agência, como o rental-car-api as cadastra. Só chegam
 *  quando o upstream traz o par; sem elas o mapa da página fica na âncora do
 *  lugar buscado. */
export const ChatUiRentalCoordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});
export type ChatUiRentalCoordinates = z.infer<typeof ChatUiRentalCoordinatesSchema>;

/**
 * Oferta de veículo para o carrossel de locação (`rental_offers`) — projeção
 * determinística do item de `search_vehicles` (produtor agents), nunca prosa do
 * LLM. `offerId` é OPACO (NUNCA renderizado): só ecoa de volta no quick-reply
 * de "Continuar". `price` é o TOTAL do período da busca, não a diária; `days`
 * (derivado dos args da busca) alimenta o rótulo "Total para N diárias". Campos
 * de mídia/agência são opcionais → o card degrada para ícone/texto.
 */
export const ChatUiRentalOfferSchema = z.object({
  offerId: z.string().min(1),
  // Nome/modelo do veículo, já sem o prefixo de status (`[...]`) do backend.
  vehicleName: z.string().optional(),
  // O "OR SIMILAR" do fornecedor como dado (BFF `cleanVehicleName`, #5529): o
  // nome vem sem ele e quem exibe traduz. O produtor agents ainda o manda no
  // texto do nome, e o kit traduz os dois.
  orSimilar: z.boolean().optional(),
  thumbUrl: z.string().optional(),
  category: z.string(),
  categoryIconUrl: z.string().optional(),
  rentalCompany: z.string(),
  // Código da locadora (`rental.id`, ex.: "MO") — o `id` da faceta de
  // locadoras. O nome pode vir com a filial; o filtro casa por este código.
  companyCode: z.string().optional(),
  companyLogoUrl: z.string().optional(),
  transmission: z.enum(['AUTOMATIC', 'MANUAL']),
  airConditioning: z.boolean(),
  price: ChatUiMoneySchema,
  days: z.number().int().min(1).optional(),
  // Período da busca (ISO-8601 local-naive), ecoado dos args — alimenta o resumo
  // da compra e o seeding do drawer de reserva client-side.
  pickupAt: z.string().optional(),
  dropoffAt: z.string().optional(),
  pickUpAgency: z.string().optional(),
  // O nome da agência tratado para o card (#5125, BFF `cleanAgencyName`): sem a
  // cidade buscada na frente, em title case, ou o endereço quando o nome é
  // genérico. O cru acima segue para o checkout, que mostra o completo.
  pickUpAgencyLabel: z.string().optional(),
  pickUpAddress: z.string().optional(),
  // As coordenadas das agências alimentam os pinos do mapa de resultados
  // (#3650) — uma agência por pino, várias ofertas por agência.
  pickUpCoordinates: ChatUiRentalCoordinatesSchema.optional(),
  dropOffAgency: z.string().optional(),
  dropOffAgencyLabel: z.string().optional(),
  dropOffAddress: z.string().optional(),
  dropOffCoordinates: ChatUiRentalCoordinatesSchema.optional(),
  // Espelha `policies.within` — dentro/fora da política corporativa.
  withinPolicy: z.boolean(),
  // Por que a oferta está entre os "Melhores carros" (curadoria determinística
  // do agente, #5642): a recomendada (menor total dentro da política), a de
  // menor preço (`cheaperBy` a menos que a recomendada, no total) ou a mais
  // perto do endereço buscado (`distanceKm`). O card escreve o selo e a nota a
  // partir destes FATOS. Ausente ⇒ sem selo.
  highlight: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('recommended') }).strict(),
      z.object({ kind: z.literal('cheapest'), cheaperBy: z.number().nonnegative() }).strict(),
      z.object({ kind: z.literal('closest'), distanceKm: z.number().nonnegative() }).strict(),
    ])
    .optional(),
});
export type ChatUiRentalOffer = z.infer<typeof ChatUiRentalOfferSchema>;

/** Faceta de categoria do drawer de refino (`rentalSearchMetadata.categories`).
 *  `id` (ex.: "SUV") é o `categoryCode` do refino server-side. */
const RentalCategoryFacetSchema = z.object({
  id: z.string(),
  name: z.string(),
  iconUrl: z.string().optional(),
  lowerValue: ChatUiMoneySchema.optional(),
  count: z.number().int().min(0),
});
export type ChatUiRentalCategoryFacet = z.infer<typeof RentalCategoryFacetSchema>;

/** Faceta de locadora do drawer de refino. `id` (ex.: "MO") é o `rentalCode`. */
const RentalCompanyFacetSchema = z.object({
  id: z.string(),
  name: z.string(),
  imageUrl: z.string().optional(),
});
export type ChatUiRentalCompanyFacet = z.infer<typeof RentalCompanyFacetSchema>;

/** Metadados da busca (`rentalSearchMetadata`) — facetas de refino, total real e
 *  a oferta sugerida ("Last order placed"). Emitido só na 1ª busca. */
const RentalSearchMetaSchema = z.object({
  countItems: z.number().int().min(0),
  categories: z.array(RentalCategoryFacetSchema),
  companies: z.array(RentalCompanyFacetSchema),
  suggestion: ChatUiRentalOfferSchema.optional(),
});
export type ChatUiRentalSearchMeta = z.infer<typeof RentalSearchMetaSchema>;

/** Página atual do card. `hasMore` = página cheia (total pós-faceta é desconhecido). */
const RentalPageSchema = z.object({
  number: z.number().int().min(1),
  hasMore: z.boolean(),
});
export type ChatUiRentalPage = z.infer<typeof RentalPageSchema>;

/** Detalhe (subtítulo) de uma política do viajante. */
const RentalPolicyDetailSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
});
export type ChatUiRentalPolicyDetail = z.infer<typeof RentalPolicyDetailSchema>;

/** Grupo de política do viajante (ex.: "Categories within politics"). */
const RentalPolicyGroupSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  subtitles: z.array(RentalPolicyDetailSchema),
});
export type ChatUiRentalPolicyGroup = z.infer<typeof RentalPolicyGroupSchema>;

/** Políticas do viajante (`rentalPolicyByTraveler`) — por-viajante, não por-oferta.
 *  Embutidas no card para o botão "Ver políticas" (sem fetch extra). */
const RentalTravelerPolicySchema = z.object({
  name: z.string(),
  policies: z.array(RentalPolicyGroupSchema).min(1),
});
export type ChatUiRentalTravelerPolicy = z.infer<typeof RentalTravelerPolicySchema>;

/** Facetas selecionadas no drawer de refino. Vazio = sem filtro. Câmbio e ar
 *  usam os valores do `RentalSearchFilter` do upstream. */
export const RentalRefineFiltersSchema = z
  .object({
    categoryCode: z.string().min(1).optional(),
    companyIds: z.array(z.string().min(1)).max(50).optional(),
    onlyPolicyWithIn: z.boolean().optional(),
    transmission: z.enum(['BOTH', 'AUTOMATIC', 'MANUAL']).optional(),
    airConditioning: z
      .enum(['BOTH', 'WITH_AIR_CONDITIONING', 'WITHOUT_AIR_CONDITIONING'])
      .optional(),
  })
  .strict();
export type RentalRefineFilters = z.infer<typeof RentalRefineFiltersSchema>;

/**
 * A página de "Ver todos os carros" de onde saiu a oferta tocada (#5833): a
 * busca do card (`searchContext`), o número e os filtros que o servidor cortou.
 * As páginas seguintes vêm do BFF e nunca entram no estado do agente, que relê
 * ESTA página no fornecedor para achar a oferta — só se a busca for a que está
 * em vigor; a de um card anterior é oferta velha. É só uma dica: a oferta
 * continua sendo a que o fornecedor devolve, e uma dica errada só não a acha.
 */
export const RentalOfferPageHintSchema = z
  .object({
    searchContext: z.string().min(1).max(64_000),
    number: z.number().int().min(1).max(100),
    filters: RentalRefineFiltersSchema.optional(),
  })
  .strict();
export type RentalOfferPageHint = z.infer<typeof RentalOfferPageHintSchema>;

/** Pedido de refino/paginação server-side (drawer → BFF, via shell). */
export interface RentalRefineRequest {
  searchContext: string;
  filters: RentalRefineFilters;
  page: number;
  /** O sujeito da política, quando não é quem está logado: o condutor
   *  escolhido no Quem. O refino repete o da busca que emitiu o contexto. */
  policyTravelerId?: string;
}

/** Resposta do refino/paginação — ofertas da página + estado de paginação. */
export interface RentalRefineResult {
  offers: ChatUiRentalOffer[];
  page: ChatUiRentalPage;
}

/** Callback determinístico de refino injetado pelo shell (web-chat → BFF). */
export type RentalRefineFn = (req: RentalRefineRequest) => Promise<RentalRefineResult>;

/** Membro `rental_offers` do `ChatUiSchema` (discriminatedUnion). Sempre
 *  server-side: `metadata` + `searchContext` + `page` acompanham toda busca. */
export const RentalOffersBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('rental_offers'),
    offers: z.array(ChatUiRentalOfferSchema).min(1).max(CHAT_UI_MAX_OFFERS),
    // Facetas + total + suggestion (rentalSearchMetadata). Habilita o drawer de
    // refino server-side + o chip "Last order placed".
    metadata: RentalSearchMetaSchema,
    // Token opaco da sessão de busca — o web-chat o devolve no POST /rental/search
    // para refinar/paginar sem o LLM. Nunca decodificado no client.
    searchContext: z.string().min(1),
    // Página corrente (refino/paginação server-side).
    page: RentalPageSchema,
    // Políticas do viajante embutidas — habilitam o botão "Ver políticas".
    // Opcional: ausente quando o upstream não devolve políticas.
    policies: RentalTravelerPolicySchema.optional(),
    // Variante de exibição. `collapsed` = o recibo da escolha (#5642): somente
    // leitura, o escolhido no topo de uma pilha (desktop) ou sozinho (celular).
    // O agente a re-emite com o MESMO `id` do card de busca → o web-chat
    // atualiza o card in-place (encolhe) em vez de empilhar uma cópia nova.
    variant: z.enum(['collapsed']).optional(),
    // `offerId` do veículo escolhido — no topo e marcado na variante collapsed.
    // Ignorado quando ausente / fora da lista.
    selectedOfferId: z.string().min(1).optional(),
  })
  .strict();
